import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Camera,
  Eye,
  Flag,
  CheckCircle2,
  ShieldCheck,
  MapPin,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { DetailCard } from '@/admin/components/shared/detail-card'
import {
  useEvidenceAnnotations,
  useVerifyPhotos,
  type EvidenceAnnotation,
} from '@/admin/hooks/use-lab-tasks'
import { PhotoLightbox, type EvidenceFile } from './photo-lightbox'
import { useEvidenceFlagCategories } from '@/admin/pages/claims/use-claim-attachments'
import { useFormatters } from '@/shared/lib/formatting'
import { AuthenticatedImage } from '@/admin/components/authenticated-image'
import { fileThumbnailUrl } from '@/shared/lib/authenticated-file'

interface Props {
  claimUuid: string
  evidence: EvidenceFile[]
  taskUuid?: string | null
  readOnly?: boolean
  userRole?: 'rs' | 'lab'
  canVerifyPhotos?: boolean
  photosVerified?: boolean
  photosVerifiedAt?: string | null
  photosVerifiedByName?: string | null
}

export function PhotoReviewPanel({
  claimUuid,
  evidence,
  taskUuid,
  readOnly = false,
  userRole = 'lab',
  canVerifyPhotos = false,
  photosVerified = false,
  photosVerifiedAt,
  photosVerifiedByName,
}: Props) {
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const { data: annotations = [] } = useEvidenceAnnotations(claimUuid)
  const { data: flagCategories = [] } = useEvidenceFlagCategories()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const verifyPhotos = useVerifyPhotos(claimUuid, taskUuid ?? '')

  const annotationMap = new Map<string, EvidenceAnnotation>()
  for (const ann of annotations) {
    annotationMap.set(ann.evidence_file_uuid, ann)
  }

  const flaggedCount = annotations.filter((a) => a.is_flagged).length
  const totalPins = annotations.reduce((sum, a) => sum + (a.pins?.length ?? 0), 0)
  const imageFiles = evidence.filter(
    (f) => f.file_url && /\.(jpg|jpeg|png|webp|gif)$/i.test(f.file_name)
  )

  if (evidence.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-light)] px-6 py-8 text-center">
        <Camera className="h-5 w-5 text-[var(--ink-3)]" />
        <p className="text-[13px] text-[var(--ink-2)]">No photos to review</p>
        <p className="text-[11px] text-[var(--ink-3)]">
          Photos uploaded by the homeowner/contractor will appear here
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Evidence card - same shared DetailCard the Resolution tab uses */}
      <DetailCard
        title={t('claims.evidenceReview')}
        icon={<Camera className="h-3.5 w-3.5" />}
        trailing={
          <span className="text-[11px] text-[var(--ink-3)]">
            {evidence.length} photos · {flaggedCount} flagged
            {totalPins > 0 && ` · ${totalPins} annotations`}
          </span>
        }
      >

      {/* Photo verification banner */}
      {photosVerified ? (
        <div className="flex items-center gap-2 rounded-[6px] border-[0.5px] border-green-200 bg-green-50 px-3.5 py-2 dark:border-green-900 dark:bg-green-950/30">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
          <span className="text-[11px] text-green-700 dark:text-green-400">
            Photos verified
            {photosVerifiedByName && ` by ${photosVerifiedByName}`}
            {photosVerifiedAt && ` · ${formatDate(photosVerifiedAt)}`}
          </span>
        </div>
      ) : canVerifyPhotos && taskUuid ? (
        <div className="flex items-center justify-between rounded-[6px] border-[0.5px] border-amber-200 bg-amber-50 px-3.5 py-2 dark:border-amber-900 dark:bg-amber-950/30">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span className="text-[11px] text-amber-700 dark:text-amber-400">
              Review all photos and verify they are sufficient for evaluation
            </span>
          </div>
          <Button
            variant="outline"
            size="xs"
            disabled={verifyPhotos.isPending}
            onClick={() => verifyPhotos.mutate()}
            className="shrink-0 border-amber-300 text-[10px] uppercase tracking-[0.08em] text-amber-700 hover:bg-amber-100"
          >
            {verifyPhotos.isPending ? 'Verifying…' : 'Verify Photos'}
          </Button>
        </div>
      ) : null}

      {/* Evidence list - matches Resolution tab evidence card */}
      <ul className="divide-y divide-[var(--rule)]">
        {evidence.map((file) => {
          const isImage = imageFiles.includes(file)
          const imageIdx = isImage ? imageFiles.indexOf(file) : -1
          const ann = annotationMap.get(file.uuid)
          const pinCount = ann?.pins?.length ?? 0
          return (
            <li key={file.uuid} className="flex items-start gap-3 px-3 py-2.5">
              <button
                type="button"
                onClick={isImage ? () => setLightboxIndex(imageIdx) : undefined}
                className={cn(
                  'relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-[var(--rule)] bg-[var(--bg-light)]',
                  isImage && 'cursor-zoom-in transition-opacity hover:opacity-80',
                  ann?.is_flagged && 'border-[#e67e22] ring-1 ring-[#e67e22]/30'
                )}
                disabled={!isImage}
              >
                <span className="text-[var(--ink-3)]">
                  <Camera className="h-3.5 w-3.5" />
                </span>
                {isImage && (
                  <AuthenticatedImage
                    src={fileThumbnailUrl(file.file_url)}
                    alt={file.file_name}
                    fallback={null}
                    className="absolute inset-0 h-full w-full"
                  />
                )}
                {ann?.is_flagged && (
                  <div className="absolute right-0.5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#e67e22]">
                    <Flag className="h-2 w-2 text-white" />
                  </div>
                )}
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12px] font-medium text-[var(--ink)]">
                    {file.file_name}
                  </span>
                  {ann?.is_flagged && (
                    <Badge
                      variant="outline"
                      className="border-[#e67e22] text-[9px] uppercase tracking-wider text-[#e67e22]"
                    >
                      <Flag className="mr-0.5 h-2.5 w-2.5" />
                      Flagged
                    </Badge>
                  )}
                  {ann?.is_flagged && ann.flag_category && (
                    <Badge variant="secondary" className="text-[9px] uppercase tracking-wider">
                      {flagCategories.find((c) => c.key === ann.flag_category)?.label ?? ann.flag_category}
                    </Badge>
                  )}
                  {pinCount > 0 && (
                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
                      <MapPin className="mr-0.5 h-2.5 w-2.5" />
                      {pinCount} pin{pinCount !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                {ann?.notes && (
                  <p className="mt-0.5 truncate text-[10px] text-[var(--ink-3)]">
                    {ann.notes}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {isImage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setLightboxIndex(imageIdx)}
                    className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      </DetailCard>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          files={imageFiles}
          annotations={annotationMap}
          currentIndex={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          claimUuid={claimUuid}
          taskUuid={taskUuid}
          readOnly={readOnly}
          userRole={userRole}
        />
      )}
    </div>
  )
}
